import { RadioGroup, RadioGroupItem } from "ui/components/radio-group.jsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "ui/components/select";

import { Badge } from "ui/components/badge";
import { Button } from "ui/components/button";
import { Calendar } from "ui/components/calendar";
import { Checkbox } from "ui/components/checkbox.jsx";
import { Controller } from "react-hook-form";
import { DatePicker } from "components/date-picker-2";
import DirectusCombobox from "components/directus/combobox";
import DirectusMultiComboboxBadges from "components/directus/multi-combobox-badges";
import { Input } from "ui/components/input";
import { Label } from "ui/components/label";
import React, { useEffect } from "react";
import RelationshipSelect from "./relationship-select.jsx";
import { Switch } from "ui/components/switch";
import { Textarea } from "ui/components/textarea";
import { cn } from "utils/index.js";
import { getFieldComponent } from "../utils/field-mapping.js";
import { readUser } from "@directus/sdk";
import { readUsers } from "@directus/sdk";
import { useState } from "react";

// Base field wrapper component
const FieldWrapper = ({
  label,
  required,
  error,
  children,
  description,
  className,
  note,
}) => (
  <div className={cn("space-y-2", className)}>
    {label && (
      <Label
        className={`text-sm font-medium gap-0 ${
          required ? "after:content-['*'] after:ml-1 after:text-red-500" : ""
        }`}
      >
        {label}
      </Label>
    )}
    {children}
    {note && <p className="text-xs text-gray-500 dark:text-gray-400">{note}</p>}
    {error && (
      <p className="text-sm text-red-500 dark:text-red-400">{error.message}</p>
    )}
  </div>
);

// Text input component
const TextInput = ({ field, fieldState, ...props }) => (
  <Input
    {...field}
    {...props}
    className={fieldState.error ? "border-red-500" : ""}
  />
);

// Textarea component
const TextareaInput = ({ field, fieldState, ...props }) => (
  <Textarea
    {...field}
    {...props}
    className={cn("min-h-32", fieldState.error ? "border-red-500" : "")}
  />
);

// Number input component
const NumberInput = ({ field, fieldState, ...props }) => (
  <Input
    {...field}
    {...props}
    type="number"
    onChange={(e) =>
      field.onChange(e.target.value ? Number(e.target.value) : "")
    }
    className={fieldState.error ? "border-red-500" : ""}
  />
);

// RadioGroup component
const RadioGroupInput = ({ field, fieldState, formField, ...props }) => (
  <RadioGroup
    onValueChange={field.onChange}
    defaultValue={field.value}
    className="grid grid-cols-1 md:grid-cols-2 gap-1"
    {...props}
  >
    {formField.choices?.map((choice) => (
      <div key={choice.value} className="flex items-center space-x-3">
        <RadioGroupItem value={choice.value} id={choice.value} />
        <Label htmlFor={choice.value}>
          {choice.text || choice.label || choice.value}
        </Label>
      </div>
    ))}
  </RadioGroup>
);

// MultiSelect component
const MultiSelectInput = ({
  field,
  fieldState,
  formField,
  client,
  ...props
}) => (
  <DirectusMultiComboboxBadges
    client={client}
    collection={formField.relatedCollection}
    value={field.value}
    onChange={field.onChange}
    placeholder={formField.placeholder || `Select ${formField.label}`}
    className={fieldState.error ? "border-red-500" : ""}
    {...props}
  />
);

// Checkbox/Switch component
const CheckboxInput = ({ field, fieldState, formField, ...props }) => (
  <div className="flex items-center space-x-2">
    <Checkbox
      checked={field.value || false}
      onCheckedChange={field.onChange}
      {...props}
    />
    <Label htmlFor={field.name}>Enabled</Label>
  </div>
);

// Select component
const SelectInput = ({ field, fieldState, formField, ...props }) => (
  <Select value={field.value || ""} onValueChange={field.onChange} {...props}>
    <SelectTrigger
      className={cn(fieldState.error ? "border-red-500" : "", "w-full")}
    >
      <SelectValue
        placeholder={formField.placeholder || `Select ${formField.label}`}
      />
    </SelectTrigger>
    <SelectContent>
      {formField.choices?.map((choice) => (
        <SelectItem key={choice.value} value={choice.value}>
          {choice.text || choice.label || choice.value}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

// Relationship Select component
const RelationshipSelectInput = ({
  field,
  fieldState,
  formField,
  client,
  ...props
}) => {
  const listFunction = {
    directus_users: readUsers,
  };

  const itemFunction = {
    directus_users: readUser,
  };

  const mapItem = (collection, item) => {
    switch (collection) {
      case "directus_users":
        return {
          value: item.id,
          label: `${item.first_name} ${item.last_name}`,
          select: `${item.first_name} ${item.last_name}`,
        };
      default:
        return {
          value: item.id,
          label: item.name || item.title,
          select: item.name || item.title,
        };
    }
  };

  const fieldsForCollections = (collection) => {
    switch (collection) {
      case "directus_users":
        return ["id", "first_name", "last_name"];

      default:
        return;
    }
  };

  const searchKeys = (collection) => {
    switch (collection) {
      case "directus_users":
        return ["first_name", "last_name"];

      default:
        return;
    }
  };

  const filtersForCollections = (collection) => {
    switch (collection) {
      case "directus_users":
        return { status: "active" };

      default:
        return;
    }
  };

  return (
    <DirectusCombobox
      client={client}
      collection={formField.relatedCollection}
      searchKeys={searchKeys(formField.relatedCollection)}
      value={field.value || null}
      filter={filtersForCollections(formField.relatedCollection)}
      listRequest={listFunction[formField.relatedCollection]}
      itemRequest={itemFunction[formField.relatedCollection]}
      fields={fieldsForCollections(formField.relatedCollection)}
      mapItem={(item) => mapItem(formField.relatedCollection, item)}
      onChange={field.onChange}
      placeholder={formField.placeholder || `Select ${formField.label}`}
      className={fieldState.error ? "border-red-500" : ""}
      {...props}
    />
  );
};

// Date input component
const DateInput = ({ field, fieldState, formField, ...props }) => {
  console.log(props);

  return (
    <DatePicker
      {...field}
      disabled={props?.disabled || false}
      className="w-full"
    />
  );
};
const FileInput = ({ field, fieldState, formField, ...props }) => {
  const [preview, setPreview] = useState(null);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    field.onChange(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
    }
  };

  return (
    <div className="space-y-2">
      <Input
        type="file"
        onChange={handleFileChange}
        className={fieldState.error ? "border-red-500" : ""}
        {...props}
      />
      {preview && (
        <div className="relative w-32 h-32">
          <img
            src={preview}
            alt="Preview"
            className="w-full h-full object-cover rounded-md"
          />
          <Button
            size="icon"
            variant="destructive"
            className="absolute top-1 right-1 h-6 w-6"
            onClick={() => {
              field.onChange(null);
              setPreview(null);
            }}
          >
            X
          </Button>
        </div>
      )}
    </div>
  );
};

// Slider component
const SliderInput = ({ field, fieldState, formField, ...props }) => (
  <Slider
    value={[field.value || 0]}
    onValueChange={(value) => field.onChange(value[0])}
    max={formField.options?.max || 100}
    min={formField.options?.min || 0}
    step={formField.options?.step || 1}
    {...props}
  />
);

// Tags component
const TagsInput = ({ field, fieldState, formField, ...props }) => {
  const [inputValue, setInputValue] = useState("");
  const tags = field.value || [];

  const handleAddTag = () => {
    if (inputValue && !tags.includes(inputValue)) {
      field.onChange([...tags, inputValue]);
      setInputValue("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    field.onChange(tags.filter((tag) => tag !== tagToRemove));
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-2">
        {tags.map((tag) => (
          <Badge key={tag} variant="outline">
            {tag}
            <button
              onClick={() => handleRemoveTag(tag)}
              className="ml-2 text-red-500"
            >
              x
            </button>
          </Badge>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleAddTag()}
          {...props}
        />
        <Button onClick={handleAddTag}>Add</Button>
      </div>
    </div>
  );
};

const JsonInput = ({ field, fieldState, ...props }) => (
  <Textarea
    {...field}
    {...props}
    value={
      typeof field.value === "string"
        ? field.value
        : JSON.stringify(field.value || {}, null, 2)
    }
    onChange={(e) => {
      try {
        const parsed = JSON.parse(e.target.value);
        field.onChange(parsed);
      } catch {
        field.onChange(e.target.value);
      }
    }}
    placeholder="Enter valid JSON"
    className={`font-mono ${fieldState.error ? "border-red-500" : ""}`}
  />
);

// Main field renderer component
export default function DirectusFieldRenderer({
  formField,
  control,
  errors,
  client,
  readonlyFields = [],
  disabled = false,
}) {
  const componentType = getFieldComponent(formField);
  const error = errors[formField.name];

  const renderField = (field, fieldState) => {
    const commonProps = {
      field,
      fieldState,
      formField,
      placeholder: formField.placeholder,
      disabled:
        disabled ||
        formField.disabled ||
        readonlyFields.includes(formField.name),
    };

    console.log(formField);

    // Handle relationship fields
    if (formField.isRelationship && formField.relatedCollection) {
      return <RelationshipSelectInput {...commonProps} client={client} />;
    }

    switch (componentType) {
      case "textarea":
        return <TextareaInput {...commonProps} />;

      case "number":
        return <NumberInput {...commonProps} />;

      case "checkbox":
      case "switch":
        return <CheckboxInput {...commonProps} />;

      case "select":
        return <SelectInput {...commonProps} />;

      case "date":
      case "time":
      case "datetime-local":
      case "datetime":
        return <DateInput {...commonProps} />;

      case "file":
        return <FileInput {...commonProps} />;

      case "json":
        return <JsonInput {...commonProps} />;

      case "password":
        return <TextInput {...commonProps} type="password" />;

      case "email":
        return <TextInput {...commonProps} type="email" />;

      case "url":
        return <TextInput {...commonProps} type="url" />;

      case "slider":
        return <SliderInput {...commonProps} />;

      case "tags":
        return <TagsInput {...commonProps} />;

      case "radio":
        return <RadioGroupInput {...commonProps} />;

      case "multiselect":
        return <MultiSelectInput {...commonProps} client={client} />;

      default:
        return <TextInput {...commonProps} />;
    }
  };

  return (
    <Controller
      name={formField.name}
      control={control}
      render={({ field, fieldState }) => (
        <FieldWrapper
          label={
            // componentType !== "checkbox" && componentType !== "switch"
            // ?
            formField.label
            // : null
          }
          required={formField.required}
          note={formField.note}
          error={error}
          description={formField.description}
          className={formField.className}
        >
          {renderField(field, fieldState)}
        </FieldWrapper>
      )}
    />
  );
}
